import * as React from 'react';
import propz from 'propz';
import { History, Location } from 'history';
import { UnsavedChangesModal } from '../../../../../components/UnsavedChangesModal/UnsavedChangesModal';
import { StatusModal } from '../../../../../components/StatusModal/StatusModal';
import {
  getNotificationSettings,
  updateNotificationSettings
} from '../../../../../helpers/service/admin/notificationSettings';
import { AppUser } from '../../../../../views/App/App';
import { Loader } from '../../../../../components/Loader/Loader';
import { Switch } from '../../../../../components/Switch/Switch';
import { HelpModal } from '../../../../../components/HelpModal/HelpModal';
import { NOTIFICATION_STATUS_TYPE, NOTIFICATION_TYPE } from '../../../../../consts/notifications';
import { Form, Formik } from 'formik';
import { registerPageHelp } from 'Src/helpers/pageHelp/pageHelp';
import './NotificationSettings.scss';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  isLoading: boolean;
  notificationSettings: {
    type: string;
    status: 'AUTO' | 'DISABLED';
  }[];
  notificationSchedule: NotificationSchedule;
  isEditMode: boolean;
  collapsedSections: Record<string, boolean>;
  isLeaveWarningOpen: boolean;
  pendingLeaveLocation: any | null;
  isBypassingNavigationBlock: boolean;

  isStatusModalOpen: boolean;
  statusModalTitle: string;
  statusModalMessage: string;
  statusModalLines: string[];

  statusModalStatus: 'success' | 'error';
  highlightedReminderType: string;

  isHelpOpen: boolean;
}

interface NotificationSchedule {
  programmeBookingOpensSoonOffsetsMinutes: number[];
  programmeBookingClosesSoonOffsetsMinutes: number[];
  eventFixtureReminderOffsetsMinutes: number[];
  eventFixtureReminderRecipients: Array<'PARENT' | 'STUDENT' | 'STAFF'>;
  eventClubReminderOffsetsMinutes: number[];
  eventClubReminderRecipients: Array<'PARENT' | 'STUDENT' | 'STAFF'>;
}

type ReminderOffsetScheduleKey =
  | 'programmeBookingOpensSoonOffsetsMinutes'
  | 'programmeBookingClosesSoonOffsetsMinutes'
  | 'eventFixtureReminderOffsetsMinutes'
  | 'eventClubReminderOffsetsMinutes';

type ReminderRecipientScheduleKey = 'eventFixtureReminderRecipients' | 'eventClubReminderRecipients';

type ReminderScheduleKey = ReminderOffsetScheduleKey | ReminderRecipientScheduleKey;

const getReminderOffsetArray = (schedule: NotificationSchedule, key: ReminderOffsetScheduleKey): number[] => {
  const value = schedule[key];
  return Array.isArray(value) ? (value as number[]) : [];
};

const AGGREGATE_TYPES = {
  PLAYER_ADDED: 'PLAYER_ADDED',
  PLAYER_REMOVED: 'PLAYER_REMOVED',
  PLAYER_DETAILS_UPDATED: 'PLAYER_DETAILS_UPDATED'
} as const;

const REMINDER_PRESET_OPTIONS = [
  { value: 10080, label: '7 days' },
  { value: 4320, label: '3 days' },
  { value: 2880, label: '2 days' },
  { value: 1440, label: '1 day' },
  { value: 720, label: '12 hours' },
  { value: 360, label: '6 hours' },
  { value: 180, label: '3 hours' },
  { value: 60, label: '1 hour' },
  { value: 30, label: '30 minutes' }
];

const CURRENT_NOTIFICATION_TYPE_ARRAY = [
  NOTIFICATION_TYPE.PERMISSION_REQUEST_ACCEPTED,
  NOTIFICATION_TYPE.PERMISSION_REQUEST_REJECTED,
  NOTIFICATION_TYPE.PERMISSION_CREATED,
  NOTIFICATION_TYPE.PERMISSION_REQUEST_CREATED,
  NOTIFICATION_TYPE.MERGE_STUDENT,
  NOTIFICATION_TYPE.MERGE_PARENT,
  NOTIFICATION_TYPE.EVENT_CREATED,
  NOTIFICATION_TYPE.EVENT_UPDATED,
  NOTIFICATION_TYPE.EVENT_CANCELED,
  NOTIFICATION_TYPE.EVENT_DETAILS_UPDATED,
  NOTIFICATION_TYPE.EVENT_DETAILS_STAFF_ADDED,
  NOTIFICATION_TYPE.EVENT_AVAILABILITY_REQUESTED,

  NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_ADDED,
  NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_REMOVED,
  NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_UPDATED,
  NOTIFICATION_TYPE.EVENT_INDIVIDUAL_PLAYER_ADDED,
  NOTIFICATION_TYPE.EVENT_INDIVIDUAL_PLAYER_REMOVED,

  NOTIFICATION_TYPE.MESSAGE_INBOX_CONSENT_REQUEST,
  NOTIFICATION_TYPE.EVENT_ELIGIBLE_STUDENTS,
  NOTIFICATION_TYPE.EVENT_CUSTOM,
  NOTIFICATION_TYPE.CLUB_CREATED,
  NOTIFICATION_TYPE.CLUB_PARTICIPATION_INVITE,
  NOTIFICATION_TYPE.CLUB_ACTIVATED,
  NOTIFICATION_TYPE.CLUB_ACTIVATED_WAITING_LIST_NOTIFICATION,

  NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_SUBMITTED,
  NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENED,
  NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENS_SOON,
  NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_CLOSES_SOON,
  NOTIFICATION_TYPE.CLUB_PROGRAMME_CLUBS_ACTIVATED,

  NOTIFICATION_TYPE.AWARD_GRANTED,
  NOTIFICATION_TYPE.INVITE_INCOMING,
  NOTIFICATION_TYPE.INVITE_ACCEPTED_BY_OPPONENT,
  NOTIFICATION_TYPE.INVITE_REJECTED_BY_OPPONENT,
  NOTIFICATION_TYPE.ABSENCE_REQUEST_SENT,
  NOTIFICATION_TYPE.ABSENCE_REQUEST_UPDATED,
  NOTIFICATION_TYPE.CUSTOM,
  NOTIFICATION_TYPE.EVENT_ATTENDANCE_ABSENT,
  NOTIFICATION_TYPE.EVENT_ATTENDANCE_PRESENT,
  NOTIFICATION_TYPE.EVENT_REPORT_ADDED,
  NOTIFICATION_TYPE.EVENT_INDIVIDUAL_SCORE_ADDED,
  NOTIFICATION_TYPE.EVENT_INDIVIDUAL_SCORE_UPDATED,
  NOTIFICATION_TYPE.EVENT_TASK_ASSIGNED_ADDED,
  NOTIFICATION_TYPE.EVENT_TASK_ASSIGNED_UPDATED,
  NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER,
  NOTIFICATION_TYPE.EVENT_CLUB_REMINDER
];

const SETTING_DESTINATIONS_TYPE = {
  REGISTRATION_AND_PROFILE_MANAGEMENT: 'REGISTRATION_AND_PROFILE_MANAGEMENT',
  USER_MANAGEMENT: 'USER_MANAGEMENT',
  EVENT_MANAGEMENT: 'EVENT_MANAGEMENT',
  CLUB_MANAGEMENT: 'CLUB_MANAGEMENT',
  CLUB_PROGRAMMES: 'CLUB_PROGRAMMES',
  ATTENDANCE_MANAGEMENT: 'ATTENDANCE_MANAGEMENT',
  AWARD_MANAGEMENT: 'AWARD_MANAGEMENT',
  INVITES_MANAGEMENT: 'INVITES_MANAGEMENT',
  ABSENCE_REQUESTS: 'ABSENCE_REQUESTS'
};

const SETTINGS_DESIGNATIONS = [
  { type: SETTING_DESTINATIONS_TYPE.REGISTRATION_AND_PROFILE_MANAGEMENT, name: 'Registration and profile management' },
  { type: SETTING_DESTINATIONS_TYPE.USER_MANAGEMENT, name: 'User management' },
  { type: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT, name: 'Event management' },
  { type: SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT, name: 'Club management' },
  { type: SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES, name: 'Activity programmes' },
  { type: SETTING_DESTINATIONS_TYPE.ATTENDANCE_MANAGEMENT, name: 'Attendance management' },
  { type: SETTING_DESTINATIONS_TYPE.AWARD_MANAGEMENT, name: 'Award management' },
  { type: SETTING_DESTINATIONS_TYPE.INVITES_MANAGEMENT, name: 'Invites management' },
  { type: SETTING_DESTINATIONS_TYPE.ABSENCE_REQUESTS, name: 'Absence requests' }
];

const SETTINGS = [
  // Registration and profile management
  {
    type: NOTIFICATION_TYPE.PERMISSION_REQUEST_ACCEPTED,
    relation: SETTING_DESTINATIONS_TYPE.REGISTRATION_AND_PROFILE_MANAGEMENT,
    name: 'User’s role request approved',
    hintText:
      'Sent to: All users\n' +
      'Sent when a user’s role request is approved by the school, letting them know they can now access the school account according to that role’s permissions.'
  },
  {
    type: NOTIFICATION_TYPE.PERMISSION_REQUEST_REJECTED,
    relation: SETTING_DESTINATIONS_TYPE.REGISTRATION_AND_PROFILE_MANAGEMENT,
    name: 'User’s role request denied',
    hintText:
      'Sent to: All users\n' +
      'Sent when a user’s role request is declined by the school, letting them know they have not been granted access to the school account.'
  },
  {
    type: NOTIFICATION_TYPE.PERMISSION_CREATED,
    relation: SETTING_DESTINATIONS_TYPE.REGISTRATION_AND_PROFILE_MANAGEMENT,
    name: 'User granted a new role',
    hintText: 'Sent to: All users\n' + 'Sent when a user is assigned an additional role on the school account.'
  },

  // User management
  {
    type: NOTIFICATION_TYPE.PERMISSION_REQUEST_CREATED,
    relation: SETTING_DESTINATIONS_TYPE.USER_MANAGEMENT,
    name: 'New role request received',
    hintText:
      'Sent to: Admin and Manager users\n' +
      'Sent when a new role request is submitted to your school. Consider disabling this temporarily when you first invite parents and/or students to sign up to avoid a high volume of notifications.'
  },
  {
    type: NOTIFICATION_TYPE.MERGE_STUDENT,
    relation: SETTING_DESTINATIONS_TYPE.USER_MANAGEMENT,
    name: 'Student account merged',
    hintText:
      'Sent to: All users\n' +
      'Sent when a student user’s role request is accepted and they are linked to the relevant inactive student account.'
  },
  {
    type: NOTIFICATION_TYPE.MERGE_PARENT,
    relation: SETTING_DESTINATIONS_TYPE.USER_MANAGEMENT,
    name: 'Parent account merged',
    hintText:
      'Sent to: All users\n' +
      'Sent when a parent user’s role request is accepted and they are linked to the relevant inactive parent account.'
  },

  // Event management
  {
    type: NOTIFICATION_TYPE.EVENT_CREATED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Event created',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent when a new event is created. Anyone on the teamsheet or linked to a participating student receives a notification.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_UPDATED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Event rescheduled',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent when an event is rescheduled. Teamsheet members, linked parents/students, and any staff added to the fixture receive a notification.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_CANCELED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Event cancelled',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent when an event is cancelled. Teamsheet members, linked parents/students, and any staff added to the fixture receive a notification.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_DETAILS_UPDATED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Event details updated',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent when key event details (time / location / age group) are changed. Teamsheet members, linked parents/students, and any staff added to the fixture receive a notification.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_DETAILS_STAFF_ADDED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'School user added to event',
    hintText:
      'Sent to: School users\n' +
      'Sent to the staff member(s) added to the event as a coach or staff member to confirm they have been added.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_AVAILABILITY_REQUESTED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Availability response requested',
    hintText:
      'Sent to: Selected parents and/or students\n' +
      'Sent when staff request an availability response, automatically request one after adding a pupil, or remind someone whose response is still pending.'
  },
  {
    type: AGGREGATE_TYPES.PLAYER_ADDED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Player added',
    hintText:
      'Sent to: Related teamsheet users\n' + 'Triggered when a student is added to a teamsheet (fixture/meet/club).'
  },
  {
    type: AGGREGATE_TYPES.PLAYER_REMOVED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Player removed',
    hintText: 'Sent to: Related teamsheet users\n' + 'Triggered when a student is removed from a teamsheet.'
  },
  {
    type: AGGREGATE_TYPES.PLAYER_DETAILS_UPDATED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Player details updated (positions, captain, substitute)',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Triggered when a player’s teamsheet details are updated: position, captaincy, or substitute status. Maps to “Team player updated”.'
  },

  {
    type: NOTIFICATION_TYPE.MESSAGE_INBOX_CONSENT_REQUEST,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Consent request sent',
    hintText:
      'Sent to: Parental users\n' +
      'Sent to all parent accounts linked to students on the teamsheet to inform them of a new parental consent request.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_REPORT_ADDED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Match report / Event notes added',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent to students and parent accounts linked to students on the teamsheet when a match report or (for clubs) event notes are added.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_INDIVIDUAL_SCORE_ADDED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Results / Scores added',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent to students and parent accounts linked to students on the teamsheet when event results (scores) are added.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_INDIVIDUAL_SCORE_UPDATED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Results / Scores updated',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent to students and parent accounts linked to students on the teamsheet when event results (scores) are updated.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_TASK_ASSIGNED_ADDED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Task assigned',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent to students and parent accounts linked to students on the teamsheet when a student is assigned a task.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_TASK_ASSIGNED_UPDATED,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Assigned task updated',
    hintText:
      'Sent to: Related teamsheet users\n' +
      'Sent to students and parent accounts linked to students on the teamsheet when a student’s assigned task is updated.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_ELIGIBLE_STUDENTS,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Challenge available to join',
    hintText:
      'Sent to: Student users\n' +
      'Sent when a self-join challenge is set up. All eligible students receive a notification.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_CUSTOM,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Custom event notification',
    hintText:
      'Sent to: Selected users\n' +
      'Allows you to compose a custom notification and choose recipients. You can only send to users related to the event’s teamsheet.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER,
    relation: SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT,
    name: 'Fixture reminder',
    hintText:
      'Sent to: Selected related event users\n' +
      'Reminder sent before a fixture starts. You can choose when the reminder is sent and who should receive it: parents, students and/or staff linked to the event.'
  },

  // Club management
  {
    type: NOTIFICATION_TYPE.CLUB_CREATED,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT,
    name: 'Club created',
    hintText:
      'Sent to: Related staff users\n' +
      'Sent when a new club is created (before activation). Only staff linked to the club receive this notification.'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_ACTIVATED,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT,
    name: 'Club activated',
    hintText:
      'Sent to: Related parental and student users\n' +
      'Sent when a club is activated. Students on the register and their parents receive a notification.'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PARTICIPATION_INVITE,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT,
    name: 'Club invitation received',
    hintText:
      'Sent to: Parental / Student users\n' +
      'Depending on your club setup, booking invitations are sent to either parents or students. Recipients are notified when they can book onto a club.'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_ACTIVATED_WAITING_LIST_NOTIFICATION,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT,
    name: 'Added to club waiting list',
    hintText:
      'Sent to: Parental / Student users\n' +
      'Sent to the configured recipient type (parents or students) when a club is activated and there is insufficient space. They are informed that they/their child has been added to the waiting list.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_CLUB_REMINDER,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT,
    name: 'Club session reminder',
    hintText:
      'Sent to: Selected users\n' +
      'Reminder sent before a club session starts. You can choose reminder intervals and whether to send it to parents, students and/or staff.'
  },

  // Activity programmes management

  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENS_SOON,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES,
    name: 'Activity programme booking opens soon',
    hintText:
      'Sent to: Programme audience\n' +
      'Reminder sent before the booking window for an activity programme opens, based on the school reminder schedule settings.'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENED,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES,
    name: 'Activity programme booking opened',
    hintText: 'Sent to: Programme audience\n' + 'Sent when the booking window for an activity programme opens.'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_CLOSES_SOON,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES,
    name: 'Activity programme booking closes soon',
    hintText:
      'Sent to: Programme audience\n' +
      'Reminder sent before the booking window for an activitiy programme closes, based on the school reminder schedule settings.'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_SUBMITTED,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES,
    name: 'Activity programme booking submitted',
    hintText:
      'Sent to: Programme audience\n' +
      'Sent when a parent or student submits their activity programme booking selections.'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_CLUBS_ACTIVATED,
    relation: SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES,
    name: 'Club allocations confirmed',
    hintText:
      'Sent to: Programme audience\n' +
      'Sent when club allocations from a club booking round have been activated, confirming the final club allocations for the student.'
  },

  // Attendance management
  {
    type: NOTIFICATION_TYPE.EVENT_ATTENDANCE_ABSENT,
    relation: SETTING_DESTINATIONS_TYPE.ATTENDANCE_MANAGEMENT,
    name: 'Student marked absent',
    hintText:
      'Sent to: Parental / Student users\n' +
      'Sent when a student is marked absent from an event. The student and any linked parents receive a notification.'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_ATTENDANCE_PRESENT,
    relation: SETTING_DESTINATIONS_TYPE.ATTENDANCE_MANAGEMENT,
    name: 'Student marked present',
    hintText:
      'Sent to: Parental / Student users\n' +
      'Sent when a student is marked present at an event. The student and any linked parents receive a notification.'
  },

  // Awards & House Points management
  {
    type: NOTIFICATION_TYPE.AWARD_GRANTED,
    relation: SETTING_DESTINATIONS_TYPE.AWARD_MANAGEMENT,
    name: 'Award / House Points granted',
    hintText:
      'Sent to: Parental / Student users\n' +
      'Sent when a student is granted an award and/or house points. The student and their parents receive a notification.'
  },

  // Invites management
  {
    type: NOTIFICATION_TYPE.INVITE_INCOMING,
    relation: SETTING_DESTINATIONS_TYPE.INVITES_MANAGEMENT,
    name: 'New incoming event invitation',
    hintText:
      'Sent to: Admin and Manager users\n' +
      'Sent when you receive a new event invitation from an opposition school or your union/association, so you can manage the invite.'
  },
  {
    type: NOTIFICATION_TYPE.INVITE_ACCEPTED_BY_OPPONENT,
    relation: SETTING_DESTINATIONS_TYPE.INVITES_MANAGEMENT,
    name: 'Opponent accepted your event invite',
    hintText:
      'Sent to: Admin and Manager users\n' + 'Sent when an opposition school accepts an event invitation you sent.'
  },
  {
    type: NOTIFICATION_TYPE.INVITE_REJECTED_BY_OPPONENT,
    relation: SETTING_DESTINATIONS_TYPE.INVITES_MANAGEMENT,
    name: 'Opponent rejected your event invite',
    hintText:
      'Sent to: Admin and Manager users\n' + 'Sent when an opposition school rejects an event invitation you sent.'
  },

  // Absence requests
  {
    type: NOTIFICATION_TYPE.ABSENCE_REQUEST_SENT,
    relation: SETTING_DESTINATIONS_TYPE.ABSENCE_REQUESTS,
    name: 'Absence report received',
    hintText: 'Sent to: Admin and Manager users\n' + 'Sent when an absence report is submitted to your school.'
  },
  {
    type: NOTIFICATION_TYPE.ABSENCE_REQUEST_UPDATED,
    relation: SETTING_DESTINATIONS_TYPE.ABSENCE_REQUESTS,
    name: 'Absence report updated',
    hintText:
      'Sent to: Parental users\n' +
      'Sent to parent accounts when the status of their submitted absence report is updated by school staff.'
  }
];

type NotificationSettingItem = {
  type: string;
  status: 'AUTO' | 'DISABLED';
};

type NotificationSettingsFormValues = {
  allSettings: NotificationSettingItem[];
  notificationSchedule: NotificationSchedule;

  programmeBookingOpensSoonCustomDays: string;
  programmeBookingOpensSoonCustomHours: string;
  programmeBookingOpensSoonCustomMinutes: string;

  programmeBookingClosesSoonCustomDays: string;
  programmeBookingClosesSoonCustomHours: string;
  programmeBookingClosesSoonCustomMinutes: string;

  eventFixtureReminderCustomDays: string;
  eventFixtureReminderCustomHours: string;
  eventFixtureReminderCustomMinutes: string;

  eventClubReminderCustomDays: string;
  eventClubReminderCustomHours: string;
  eventClubReminderCustomMinutes: string;

  isRegistrationAndProfileManagement: boolean;
  isUserManagement: boolean;
  isEventManagement: boolean;
  isClubManagement: boolean;
  isClubProgrammes: boolean;
  isAttendanceManagement: boolean;
  isAwardManagement: boolean;
  isInvitesManagement: boolean;
  isAbsenceRequests: boolean;
  isAllNotifications: boolean;
};

type PendingLeaveState = { isOpen: false; nextLocation: null } | { isOpen: true; nextLocation: any };

const normalizeReminderOffsets = (input: number[]): number[] => {
  if (!Array.isArray(input)) return [];

  return Array.from(
    new Set(
      input
        .map(v => Number(v))
        .filter(v => Number.isFinite(v) && v > 0)
        .map(v => Math.round(v))
    )
  ).sort((a, b) => b - a);
};

const formatReminderOffsetLabel = (minutes: number): string => {
  const safeMinutes = Math.round(Number(minutes) || 0);

  if (safeMinutes <= 0) return '';

  const days = Math.floor(safeMinutes / 1440);
  const remainingAfterDays = safeMinutes % 1440;
  const hours = Math.floor(remainingAfterDays / 60);
  const mins = remainingAfterDays % 60;

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} day${days === 1 ? '' : 's'}`);
  }

  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  }

  if (mins > 0) {
    parts.push(`${mins} minute${mins === 1 ? '' : 's'}`);
  }

  return parts.join(' ');
};

const isPresetReminderOffset = (minutes: number): boolean => {
  return REMINDER_PRESET_OPTIONS.some(option => option.value === minutes);
};

export class NotificationSettings extends React.Component<Props, State> {
  private formValuesRef: NotificationSettingsFormValues | null = null;
  private unblockNavigation: (() => void) | null = null;
  private unregisterPageHelp: (() => void) | null = null;
  private sectionRefs: Partial<Record<string, HTMLDivElement | null>> = {};

  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      notificationSettings: [],
      notificationSchedule: {
        programmeBookingOpensSoonOffsetsMinutes: [],
        programmeBookingClosesSoonOffsetsMinutes: [],

        eventFixtureReminderOffsetsMinutes: [],
        eventFixtureReminderRecipients: [],

        eventClubReminderOffsetsMinutes: [],
        eventClubReminderRecipients: []
      },
      isEditMode: false,
      collapsedSections: {
        [SETTING_DESTINATIONS_TYPE.REGISTRATION_AND_PROFILE_MANAGEMENT]: true,
        [SETTING_DESTINATIONS_TYPE.USER_MANAGEMENT]: true,
        [SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT]: true,
        [SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT]: true,
        [SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES]: true,
        [SETTING_DESTINATIONS_TYPE.ATTENDANCE_MANAGEMENT]: true,
        [SETTING_DESTINATIONS_TYPE.AWARD_MANAGEMENT]: true,
        [SETTING_DESTINATIONS_TYPE.INVITES_MANAGEMENT]: true,
        [SETTING_DESTINATIONS_TYPE.ABSENCE_REQUESTS]: true
      },
      isLeaveWarningOpen: false,
      pendingLeaveLocation: null,
      isBypassingNavigationBlock: false,

      isStatusModalOpen: false,
      statusModalTitle: '',
      statusModalMessage: '',
      statusModalLines: [],
      statusModalStatus: 'success',
      highlightedReminderType: '',

      isHelpOpen: false
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });
    this.getNotificationSettingsFromNotificationSettingsObject();

    this.unblockNavigation = this.props.history.block(nextLocation => {
      if (this.state.isBypassingNavigationBlock) {
        return;
      }

      if (!this.hasUnsavedChanges()) {
        return;
      }

      this.setState({
        isLeaveWarningOpen: true,
        pendingLeaveLocation: nextLocation
      });

      return false;
    });

    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.unregisterPageHelp = registerPageHelp(this.openHelpModal, 'Help for notification settings');
  }

  componentWillUnmount() {
    if (this.unblockNavigation) {
      this.unblockNavigation();
      this.unblockNavigation = null;
    }

    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.unregisterPageHelp) this.unregisterPageHelp();
  }

  openHelpModal = (): void => {
    this.setState({ isHelpOpen: true });
  };

  handleBeforeUnload = (event: BeforeUnloadEvent): string | void => {
    if (!this.hasUnsavedChanges()) return;

    event.preventDefault();
    event.returnValue = '';
    return '';
  };

  getNotificationSettingsFromNotificationSettingsObject = (): void => {
    const user = this.props.user;

    getNotificationSettings(user).then(notificationSettingsResponse => {
      const notificationsObject = propz.get(notificationSettingsResponse, ['notifications'], {});

      const notificationSchedule = propz.get(
        notificationSettingsResponse,
        ['notificationSchedule'],
        {} as NotificationSchedule
      ) as NotificationSchedule;

      const notificationSettings = this.getArrayFromObjectDesiredValues(notificationsObject);

      this.setState({
        notificationSettings,
        notificationSchedule: {
          programmeBookingOpensSoonOffsetsMinutes: normalizeReminderOffsets(
            notificationSchedule.programmeBookingOpensSoonOffsetsMinutes
          ),
          programmeBookingClosesSoonOffsetsMinutes: normalizeReminderOffsets(
            notificationSchedule.programmeBookingClosesSoonOffsetsMinutes
          ),

          eventFixtureReminderOffsetsMinutes: normalizeReminderOffsets(
            notificationSchedule.eventFixtureReminderOffsetsMinutes
          ),
          eventFixtureReminderRecipients: Array.isArray(notificationSchedule.eventFixtureReminderRecipients)
            ? notificationSchedule.eventFixtureReminderRecipients
            : [],

          eventClubReminderOffsetsMinutes: normalizeReminderOffsets(
            notificationSchedule.eventClubReminderOffsetsMinutes
          ),
          eventClubReminderRecipients: Array.isArray(notificationSchedule.eventClubReminderRecipients)
            ? notificationSchedule.eventClubReminderRecipients
            : []
        },
        isLoading: false
      });
    });
  };

  getArrayFromObjectDesiredValues = (
    object: Record<string, 'AUTO' | 'DISABLED'>
  ): { type: string; status: 'AUTO' | 'DISABLED' }[] => {
    const arrayWithData: { type: string; status: 'AUTO' | 'DISABLED' }[] = [];
    const copyObject = { ...object };

    for (let key in copyObject) {
      const existType = CURRENT_NOTIFICATION_TYPE_ARRAY.find(type => type === key);
      if (typeof existType !== 'undefined') {
        arrayWithData.push({ type: key, status: copyObject[key] });
      }
    }

    const get = (k: string) => (copyObject[k] ?? 'DISABLED') as 'AUTO' | 'DISABLED';
    const bothAuto = (a: string, b: string) => (get(a) === 'AUTO' && get(b) === 'AUTO' ? 'AUTO' : 'DISABLED');

    arrayWithData.push({
      type: AGGREGATE_TYPES.PLAYER_ADDED,
      status: bothAuto(NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_ADDED, NOTIFICATION_TYPE.EVENT_INDIVIDUAL_PLAYER_ADDED)
    });

    arrayWithData.push({
      type: AGGREGATE_TYPES.PLAYER_REMOVED,
      status: bothAuto(NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_REMOVED, NOTIFICATION_TYPE.EVENT_INDIVIDUAL_PLAYER_REMOVED)
    });

    arrayWithData.push({
      type: AGGREGATE_TYPES.PLAYER_DETAILS_UPDATED,
      status: get(NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_UPDATED)
    });

    return arrayWithData;
  };

  getNotificationSettingsObjectFromNotificationSettings = (
    arrayWithData: { type: string; status: 'AUTO' | 'DISABLED' }[]
  ): Record<string, 'AUTO' | 'DISABLED'> => {
    const objectData: Record<string, 'AUTO' | 'DISABLED'> = {};
    arrayWithData.forEach(item => {
      if (
        item.type !== AGGREGATE_TYPES.PLAYER_ADDED &&
        item.type !== AGGREGATE_TYPES.PLAYER_REMOVED &&
        item.type !== AGGREGATE_TYPES.PLAYER_DETAILS_UPDATED
      ) {
        objectData[item.type] = item.status;
      }
    });
    return objectData;
  };

  getNotificationStatus = (status: boolean): string => {
    return status ? NOTIFICATION_STATUS_TYPE.AUTO : NOTIFICATION_STATUS_TYPE.DISABLED;
  };

  getReverseStatus = (status: string): string => {
    return status === NOTIFICATION_STATUS_TYPE.AUTO ? NOTIFICATION_STATUS_TYPE.DISABLED : NOTIFICATION_STATUS_TYPE.AUTO;
  };

  getIsStatusAuto = (status: string): boolean => {
    return status === NOTIFICATION_STATUS_TYPE.AUTO;
  };

  getShortHintText = (hintText: string): string => {
    const text = String(hintText || '').trim();
    if (!text) return '';

    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    const withoutSentTo = lines.filter(line => !/^Sent to:/i.test(line));

    if (withoutSentTo.length > 0) {
      return withoutSentTo[0];
    }

    return lines[0] || '';
  };

  getRecipientLabel = (hintText: string): string => {
    const text = String(hintText || '').trim();
    if (!text) return '';

    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);

    const sentToLine = lines.find(line => /^Sent to:/i.test(line));
    if (!sentToLine) return '';

    return sentToLine.replace(/^Sent to:\s*/i, '').trim();
  };

  private getReminderSectionByKey = (scheduleKey: ReminderScheduleKey): string => {
    switch (scheduleKey) {
      case 'programmeBookingOpensSoonOffsetsMinutes':
      case 'programmeBookingClosesSoonOffsetsMinutes':
        return SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES;

      case 'eventFixtureReminderOffsetsMinutes':
      case 'eventFixtureReminderRecipients':
        return SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT;

      case 'eventClubReminderOffsetsMinutes':
      case 'eventClubReminderRecipients':
        return SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT;

      default:
        return SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT;
    }
  };

  toggleReminderOffset = (
    values: NotificationSettingsFormValues,
    setFieldValue,
    scheduleKey: ReminderOffsetScheduleKey,
    minutes: number
  ): void => {
    this.expandSection(this.getReminderSectionByKey(scheduleKey));
    const current = getReminderOffsetArray(values.notificationSchedule, scheduleKey);

    const next: number[] = current.includes(minutes)
      ? current.filter(v => v !== minutes)
      : normalizeReminderOffsets([...current, minutes]);

    setFieldValue(`notificationSchedule.${scheduleKey}`, next);
  };

  addCustomReminderOffset = (
    values: NotificationSettingsFormValues,
    setFieldValue,
    scheduleKey: ReminderOffsetScheduleKey,
    inputPrefix:
      | 'programmeBookingOpensSoonCustom'
      | 'programmeBookingClosesSoonCustom'
      | 'eventFixtureReminderCustom'
      | 'eventClubReminderCustom'
  ): void => {
    this.expandSection(this.getReminderSectionByKey(scheduleKey));
    const rawDays = String(values[`${inputPrefix}Days`] || '').trim();
    const rawHours = String(values[`${inputPrefix}Hours`] || '').trim();
    const rawMinutes = String(values[`${inputPrefix}Minutes`] || '').trim();

    const days = rawDays === '' ? 0 : Number(rawDays);
    const hours = rawHours === '' ? 0 : Number(rawHours);
    const minutes = rawMinutes === '' ? 0 : Number(rawMinutes);

    if (
      !Number.isFinite(days) ||
      !Number.isFinite(hours) ||
      !Number.isFinite(minutes) ||
      days < 0 ||
      hours < 0 ||
      minutes < 0
    ) {
      return;
    }

    const totalMinutes = Math.round(days * 1440 + hours * 60 + minutes);

    if (totalMinutes <= 0) {
      return;
    }

    const current = getReminderOffsetArray(values.notificationSchedule, scheduleKey);
    const next: number[] = normalizeReminderOffsets([...current, totalMinutes]);

    setFieldValue(`notificationSchedule.${scheduleKey}`, next);
    setFieldValue(`${inputPrefix}Days`, '');
    setFieldValue(`${inputPrefix}Hours`, '');
    setFieldValue(`${inputPrefix}Minutes`, '');
  };

  removeReminderOffset = (
    values: NotificationSettingsFormValues,
    setFieldValue,
    scheduleKey: ReminderOffsetScheduleKey,
    minutes: number
  ): void => {
    this.expandSection(this.getReminderSectionByKey(scheduleKey));
    const current = getReminderOffsetArray(values.notificationSchedule, scheduleKey);
    const next: number[] = current.filter(v => v !== minutes);
    setFieldValue(`notificationSchedule.${scheduleKey}`, next);
  };

  renderReminderScheduleInline = (
    values: NotificationSettingsFormValues,
    setFieldValue,
    scheduleKey: ReminderOffsetScheduleKey,
    inputPrefix:
      | 'programmeBookingOpensSoonCustom'
      | 'programmeBookingClosesSoonCustom'
      | 'eventFixtureReminderCustom'
      | 'eventClubReminderCustom',
    title: string
  ): React.ReactNode => {
    const selectedValues = getReminderOffsetArray(values.notificationSchedule, scheduleKey);
    const customValues = selectedValues.filter(value => !isPresetReminderOffset(value));
    const customDays = values[`${inputPrefix}Days`] || '';
    const customHours = values[`${inputPrefix}Hours`] || '';
    const customMinutes = values[`${inputPrefix}Minutes`] || '';

    const previewDays = customDays === '' ? 0 : Number(customDays);
    const previewHours = customHours === '' ? 0 : Number(customHours);
    const previewMinutes = customMinutes === '' ? 0 : Number(customMinutes);

    const previewTotalMinutes =
      (Number.isFinite(previewDays) ? previewDays : 0) * 1440 +
      (Number.isFinite(previewHours) ? previewHours : 0) * 60 +
      (Number.isFinite(previewMinutes) ? previewMinutes : 0);

    const hasPreviewValue = previewTotalMinutes > 0;

    return (
      <div className="nsScheduleCard">
        <div className="nsScheduleTitle">{title}</div>
        <div className="nsScheduleText">Choose when this reminder should be sent.</div>

        <div className="nsPresetList">
          {REMINDER_PRESET_OPTIONS.map(option => {
            const isSelected = selectedValues.includes(option.value);

            return (
              <button
                key={`${scheduleKey}_${option.value}`}
                type="button"
                className={`nsPresetChip ${isSelected ? 'is-selected' : ''}`}
                aria-pressed={isSelected}
                disabled={!this.state.isEditMode}
                onClick={() => this.toggleReminderOffset(values, setFieldValue, scheduleKey, option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="nsScheduleEditor">
          <div className="nsCustomBuilder">
            <div className="nsCustomField">
              <div className="nsCustomLabel">Days</div>
              <input
                type="number"
                min={0}
                step={1}
                value={customDays}
                disabled={!this.state.isEditMode}
                onChange={event => setFieldValue(`${inputPrefix}Days`, event.target.value)}
                className="nsCustomInput"
                placeholder="0"
              />
            </div>

            <div className="nsCustomField">
              <div className="nsCustomLabel">Hours</div>
              <input
                type="number"
                min={0}
                step={1}
                value={customHours}
                disabled={!this.state.isEditMode}
                onChange={event => setFieldValue(`${inputPrefix}Hours`, event.target.value)}
                className="nsCustomInput"
                placeholder="0"
              />
            </div>

            <div className="nsCustomField">
              <div className="nsCustomLabel">Minutes</div>
              <input
                type="number"
                min={0}
                step={1}
                value={customMinutes}
                disabled={!this.state.isEditMode}
                onChange={event => setFieldValue(`${inputPrefix}Minutes`, event.target.value)}
                className="nsCustomInput"
                placeholder="0"
              />
            </div>

            <button
              type="button"
              className="nsCustomAction"
              disabled={!this.state.isEditMode}
              onClick={() => this.addCustomReminderOffset(values, setFieldValue, scheduleKey, inputPrefix)}
            >
              Add custom interval
            </button>
          </div>

          <div className="nsCustomHint">Add a custom reminder interval using days, hours and minutes.</div>

          {hasPreviewValue ? (
            <div className="nsPreviewLine">
              Preview: <span className="nsPreviewStrong">{formatReminderOffsetLabel(previewTotalMinutes)}</span>
            </div>
          ) : null}

          {customValues.length > 0 ? (
            <div className="nsChipRow">
              {customValues.map(value => (
                <div key={`${scheduleKey}_custom_${value}`} className="nsChip nsChip--custom">
                  <span>{formatReminderOffsetLabel(value)}</span>
                  <button
                    type="button"
                    className="nsChipRemove"
                    disabled={!this.state.isEditMode}
                    onClick={() => this.removeReminderOffset(values, setFieldValue, scheduleKey, value)}
                    title="Remove custom interval"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="nsSelectedText">
          Selected:{' '}
          {selectedValues.length > 0
            ? selectedValues.map(formatReminderOffsetLabel).join(', ')
            : 'No reminder intervals selected'}
        </div>
      </div>
    );
  };

  toggleReminderRecipient = (
    values: NotificationSettingsFormValues,
    setFieldValue,
    scheduleKey: ReminderRecipientScheduleKey,
    recipient: 'PARENT' | 'STUDENT' | 'STAFF'
  ): void => {
    this.expandSection(this.getReminderSectionByKey(scheduleKey));

    const current = Array.isArray(values.notificationSchedule[scheduleKey])
      ? values.notificationSchedule[scheduleKey]
      : [];

    const next = current.includes(recipient) ? current.filter(item => item !== recipient) : [...current, recipient];

    setFieldValue(`notificationSchedule.${scheduleKey}`, next);
  };

  renderReminderRecipientsInline = (
    values: NotificationSettingsFormValues,
    setFieldValue,
    scheduleKey: 'eventFixtureReminderRecipients' | 'eventClubReminderRecipients',
    title: string
  ): React.ReactNode => {
    const selected = Array.isArray(values.notificationSchedule[scheduleKey])
      ? values.notificationSchedule[scheduleKey]
      : [];

    const options: Array<{ value: 'PARENT' | 'STUDENT' | 'STAFF'; label: string }> = [
      { value: 'PARENT', label: 'Parents' },
      { value: 'STUDENT', label: 'Students' },
      { value: 'STAFF', label: 'Staff' }
    ];

    return (
      <div className="nsScheduleCard">
        <div className="nsScheduleTitle">{title}</div>
        <div className="nsScheduleText">Choose which related users should receive this reminder.</div>

        <div className="nsPresetList">
          {options.map(option => {
            const isSelected = selected.includes(option.value);

            return (
              <button
                key={`${scheduleKey}_${option.value}`}
                type="button"
                className={`nsPresetChip ${isSelected ? 'is-selected' : ''}`}
                aria-pressed={isSelected}
                disabled={!this.state.isEditMode}
                onClick={() => this.toggleReminderRecipient(values, setFieldValue, scheduleKey, option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div className="nsSelectedText">
          Selected: {selected.length > 0 ? selected.join(', ') : 'No recipients selected'}
        </div>
      </div>
    );
  };

  toggleSectionCollapsed = (sectionType: string): void => {
    this.setState(prevState => ({
      collapsedSections: {
        ...prevState.collapsedSections,
        [sectionType]: !prevState.collapsedSections[sectionType]
      }
    }));
  };

  expandAllSections = (): void => {
    const nextCollapsedSections = {};

    SETTINGS_DESIGNATIONS.forEach(setting => {
      nextCollapsedSections[setting.type] = false;
    });

    this.setState({
      collapsedSections: nextCollapsedSections
    });
  };

  collapseAllSections = (): void => {
    const nextCollapsedSections = {};

    SETTINGS_DESIGNATIONS.forEach(setting => {
      nextCollapsedSections[setting.type] = true;
    });

    this.setState({
      collapsedSections: nextCollapsedSections
    });
  };

  expandSection = (sectionType: string): void => {
    this.setState(prevState => ({
      collapsedSections: {
        ...prevState.collapsedSections,
        [sectionType]: false
      }
    }));
  };

  onCloseClick = (): void => {
    if (this.hasUnsavedChanges()) {
      this.setState({ isLeaveWarningOpen: true, pendingLeaveLocation: null });
      return;
    }

    this.discardChangesAndClose();
  };

  discardChangesAndClose = (): void => {
    this.setState({
      isEditMode: false,
      isLoading: true,
      highlightedReminderType: '',
      isStatusModalOpen: false,
      statusModalTitle: '',
      statusModalMessage: '',
      statusModalLines: [],
      statusModalStatus: 'success'
    });
    this.getNotificationSettingsFromNotificationSettingsObject();
  };

  onFormSubmit = (values): void => {
    const { user } = this.props;
    const { isEditMode } = this.state;

    if (isEditMode) {
      this.setState({ isLoading: true });

      const base = this.getNotificationSettingsObjectFromNotificationSettings(values.allSettings);

      const getAgg = (t: string) => {
        const found = values.allSettings.find(s => s.type === t);
        return found ? found.status : NOTIFICATION_STATUS_TYPE.DISABLED;
      };

      const playerAdded = getAgg(AGGREGATE_TYPES.PLAYER_ADDED);
      const playerRemoved = getAgg(AGGREGATE_TYPES.PLAYER_REMOVED);
      const playerUpdated = getAgg(AGGREGATE_TYPES.PLAYER_DETAILS_UPDATED);

      const data = {
        ...base,
        [NOTIFICATION_TYPE.EVENT_INDIVIDUAL_PLAYER_ADDED]: playerAdded,
        [NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_ADDED]: playerAdded,

        [NOTIFICATION_TYPE.EVENT_INDIVIDUAL_PLAYER_REMOVED]: playerRemoved,
        [NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_REMOVED]: playerRemoved,

        [NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_UPDATED]: playerUpdated,
        notificationSchedule: {
          programmeBookingOpensSoonOffsetsMinutes: values.notificationSchedule.programmeBookingOpensSoonOffsetsMinutes,
          programmeBookingClosesSoonOffsetsMinutes:
            values.notificationSchedule.programmeBookingClosesSoonOffsetsMinutes,
          eventFixtureReminderOffsetsMinutes: values.notificationSchedule.eventFixtureReminderOffsetsMinutes,
          eventFixtureReminderRecipients: values.notificationSchedule.eventFixtureReminderRecipients,
          eventClubReminderOffsetsMinutes: values.notificationSchedule.eventClubReminderOffsetsMinutes,
          eventClubReminderRecipients: values.notificationSchedule.eventClubReminderRecipients
        }
      };

      const fixtureReminderStatus = data[NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER];
      const clubReminderStatus = data[NOTIFICATION_TYPE.EVENT_CLUB_REMINDER];
      const programmeOpensSoonStatus = data[NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENS_SOON];
      const programmeClosesSoonStatus = data[NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_CLOSES_SOON];

      const fixtureOffsets = normalizeReminderOffsets(values.notificationSchedule.eventFixtureReminderOffsetsMinutes);
      const fixtureRecipients = Array.isArray(values.notificationSchedule.eventFixtureReminderRecipients)
        ? values.notificationSchedule.eventFixtureReminderRecipients
        : [];

      const clubOffsets = normalizeReminderOffsets(values.notificationSchedule.eventClubReminderOffsetsMinutes);
      const clubRecipients = Array.isArray(values.notificationSchedule.eventClubReminderRecipients)
        ? values.notificationSchedule.eventClubReminderRecipients
        : [];

      const programmeOpensSoonOffsets = normalizeReminderOffsets(
        values.notificationSchedule.programmeBookingOpensSoonOffsetsMinutes
      );

      const programmeClosesSoonOffsets = normalizeReminderOffsets(
        values.notificationSchedule.programmeBookingClosesSoonOffsetsMinutes
      );

      let validationError = '';
      let validationSection = '';
      let highlightedReminderType = '';

      if (fixtureReminderStatus === NOTIFICATION_STATUS_TYPE.AUTO) {
        if (fixtureOffsets.length === 0) {
          validationError = 'Fixture reminder cannot be enabled without at least one reminder interval.';
          validationSection = SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT;
          highlightedReminderType = NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER;
        } else if (fixtureRecipients.length === 0) {
          validationError = 'Fixture reminder cannot be enabled without at least one recipient.';
          validationSection = SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT;
          highlightedReminderType = NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER;
        }
      } else if (clubReminderStatus === NOTIFICATION_STATUS_TYPE.AUTO) {
        if (clubOffsets.length === 0) {
          validationError = 'Club reminder cannot be enabled without at least one reminder interval.';
          validationSection = SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT;
          highlightedReminderType = NOTIFICATION_TYPE.EVENT_CLUB_REMINDER;
        } else if (clubRecipients.length === 0) {
          validationError = 'Club reminder cannot be enabled without at least one recipient.';
          validationSection = SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT;
          highlightedReminderType = NOTIFICATION_TYPE.EVENT_CLUB_REMINDER;
        }
      } else if (programmeOpensSoonStatus === NOTIFICATION_STATUS_TYPE.AUTO && programmeOpensSoonOffsets.length === 0) {
        validationError =
          'Activity programme booking opens soon reminder cannot be enabled without at least one reminder interval.';
        validationSection = SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES;
        highlightedReminderType = NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENS_SOON;
      } else if (
        programmeClosesSoonStatus === NOTIFICATION_STATUS_TYPE.AUTO &&
        programmeClosesSoonOffsets.length === 0
      ) {
        validationError =
          'Activity programme booking closes soon reminder cannot be enabled without at least one reminder interval.';
        validationSection = SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES;
        highlightedReminderType = NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_CLOSES_SOON;
      }

      if (validationError) {
        this.setState(
          prevState => ({
            isLoading: false,
            isStatusModalOpen: true,
            statusModalStatus: 'error',
            statusModalTitle: 'Unable to save notification settings',
            statusModalMessage: validationError,
            statusModalLines: [],
            highlightedReminderType,
            collapsedSections: validationSection
              ? {
                  ...prevState.collapsedSections,
                  [validationSection]: false
                }
              : prevState.collapsedSections
          }),
          () => {
            if (validationSection) {
              const element = this.sectionRefs[validationSection];
              if (element && typeof element.scrollIntoView === 'function') {
                element.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }
            }
          }
        );
        return;
      }

      updateNotificationSettings(user, data)
        .then(notificationSettingsResponse => {
          const notificationsObject = propz.get(notificationSettingsResponse, ['notifications'], {});

          const notificationSchedule = propz.get(
            notificationSettingsResponse,
            ['notificationSchedule'],
            {} as NotificationSchedule
          ) as NotificationSchedule;

          const arrayNotificationSettings = this.getArrayFromObjectDesiredValues(notificationsObject);

          this.setState({
            notificationSettings: arrayNotificationSettings,
            notificationSchedule: {
              programmeBookingOpensSoonOffsetsMinutes: normalizeReminderOffsets(
                notificationSchedule.programmeBookingOpensSoonOffsetsMinutes
              ),
              programmeBookingClosesSoonOffsetsMinutes: normalizeReminderOffsets(
                notificationSchedule.programmeBookingClosesSoonOffsetsMinutes
              ),
              eventFixtureReminderOffsetsMinutes: normalizeReminderOffsets(
                notificationSchedule.eventFixtureReminderOffsetsMinutes
              ),
              eventFixtureReminderRecipients: Array.isArray(notificationSchedule.eventFixtureReminderRecipients)
                ? notificationSchedule.eventFixtureReminderRecipients
                : [],
              eventClubReminderOffsetsMinutes: normalizeReminderOffsets(
                notificationSchedule.eventClubReminderOffsetsMinutes
              ),
              eventClubReminderRecipients: Array.isArray(notificationSchedule.eventClubReminderRecipients)
                ? notificationSchedule.eventClubReminderRecipients
                : []
            },
            isEditMode: false,
            isLoading: false,
            isLeaveWarningOpen: false,
            pendingLeaveLocation: null,
            isStatusModalOpen: true,
            statusModalStatus: 'success',
            statusModalTitle: 'Notification settings saved',
            statusModalMessage: 'Your changes have been saved successfully.',
            statusModalLines: ['The updated notification settings are now active for your school.'],
            highlightedReminderType: ''
          });
        })
        .catch(err => {
          console.error('Notification settings save error:', err);

          const errorText = propz.get(
            err,
            ['response', 'data', 'details', 'text'],
            propz.get(err, ['response', 'data', 'error'], 'Failed to save notification settings.')
          );

          let validationSection = '';
          let highlightedReminderType = '';

          if (/fixture reminder/i.test(errorText)) {
            validationSection = SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT;
            highlightedReminderType = NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER;
          } else if (/club reminder/i.test(errorText)) {
            validationSection = SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT;
            highlightedReminderType = NOTIFICATION_TYPE.EVENT_CLUB_REMINDER;
          } else if (/booking opens soon/i.test(errorText)) {
            validationSection = SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES;
            highlightedReminderType = NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENS_SOON;
          } else if (/booking closes soon/i.test(errorText)) {
            validationSection = SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES;
            highlightedReminderType = NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_CLOSES_SOON;
          }

          this.setState(
            prevState => ({
              isLoading: false,
              isStatusModalOpen: true,
              statusModalStatus: 'error',
              statusModalTitle: 'Unable to save notification settings',
              statusModalMessage: errorText,
              statusModalLines: [],
              highlightedReminderType,
              collapsedSections: validationSection
                ? {
                    ...prevState.collapsedSections,
                    [validationSection]: false
                  }
                : prevState.collapsedSections
            }),
            () => {
              if (validationSection) {
                const element = this.sectionRefs[validationSection];
                if (element && typeof element.scrollIntoView === 'function') {
                  element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }
            }
          );
        });
    } else {
      this.setState({ isEditMode: true });
    }
  };

  onAllNotificationsChange = (setFieldValue, values): void => {
    const { isAllNotifications } = values;
    const changedAllSettings = values.allSettings.map(setting => {
      const { status, ...rest } = setting;
      return {
        ...rest,
        status: this.getNotificationStatus(!isAllNotifications)
      };
    });
    setFieldValue('isAllNotifications', !isAllNotifications);
    setFieldValue('isRegistrationAndProfileManagement', !isAllNotifications);
    setFieldValue('isUserManagement', !isAllNotifications);
    setFieldValue('isEventManagement', !isAllNotifications);
    setFieldValue('isClubManagement', !isAllNotifications);
    setFieldValue('isClubProgrammes', !isAllNotifications);
    setFieldValue('isAttendanceManagement', !isAllNotifications);
    setFieldValue('isAwardManagement', !isAllNotifications);
    setFieldValue('isInvitesManagement', !isAllNotifications);
    setFieldValue('isAbsenceRequests', !isAllNotifications);
    setFieldValue('allSettings', changedAllSettings);
  };

  onGroupSettingsChange = (
    values,
    setFieldValue,
    checkType: string,
    groupSetting: { type: string; name: string }
  ): void => {
    const copyAllSettings = [...values.allSettings];
    const settings = SETTINGS.filter(setting => setting.relation === groupSetting.type);
    const checkedSettings = [...copyAllSettings].filter(adjustment => {
      return settings.some(setting => setting.type === adjustment.type);
    });

    const checkedCopySettings = copyAllSettings.map(copySetting => {
      const isExistSetting = checkedSettings.some(checkedSetting => checkedSetting.type === copySetting.type);
      if (isExistSetting) {
        const checkedSetting = { ...copySetting };
        checkedSetting.status = this.getNotificationStatus(!values[checkType]);
        return checkedSetting;
      } else {
        return copySetting;
      }
    });
    const isAutoAllSettings = this.getAllNotificationsStatus(checkedCopySettings);
    setFieldValue('isAllNotifications', isAutoAllSettings);
    setFieldValue('allSettings', checkedCopySettings);
    setFieldValue(checkType, !values[checkType]);
  };

  getCheckType = (type: string): string => {
    switch (type) {
      case SETTING_DESTINATIONS_TYPE.REGISTRATION_AND_PROFILE_MANAGEMENT:
        return 'isRegistrationAndProfileManagement';
      case SETTING_DESTINATIONS_TYPE.USER_MANAGEMENT:
        return 'isUserManagement';
      case SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT:
        return 'isEventManagement';
      case SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT:
        return 'isClubManagement';
      case SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES:
        return 'isClubProgrammes';
      case SETTING_DESTINATIONS_TYPE.ATTENDANCE_MANAGEMENT:
        return 'isAttendanceManagement';
      case SETTING_DESTINATIONS_TYPE.AWARD_MANAGEMENT:
        return 'isAwardManagement';
      case SETTING_DESTINATIONS_TYPE.INVITES_MANAGEMENT:
        return 'isInvitesManagement';
      case SETTING_DESTINATIONS_TYPE.ABSENCE_REQUESTS:
        return 'isAbsenceRequests';
    }
  };

  getAllNotificationsStatus = (notificationSettings: { type: string; status: 'AUTO' | 'DISABLED' }[]): boolean => {
    const isAutoAllSettings = [...notificationSettings].every(
      setting => setting.status === NOTIFICATION_STATUS_TYPE.AUTO
    );
    return isAutoAllSettings;
  };

  getGroupSettingStatus = (
    notificationSettings: { type: string; status: 'AUTO' | 'DISABLED' }[],
    type: string
  ): boolean => {
    const copyAllSettings = [...notificationSettings];
    const settings = [...SETTINGS].filter(setting => setting.relation === type);
    const someSettings = [...copyAllSettings].filter(adjustment => {
      return settings.some(setting => setting.type === adjustment.type);
    });
    const isAutoCurrentSettings = someSettings.every(setting => setting.status === NOTIFICATION_STATUS_TYPE.AUTO);

    return isAutoCurrentSettings;
  };

  getGroupVisualState = (
    notificationSettings: { type: string; status: 'AUTO' | 'DISABLED' }[],
    type: string
  ): 'all-on' | 'all-off' | 'mixed' => {
    const settings = SETTINGS.filter(setting => setting.relation === type);
    const groupItems = notificationSettings.filter(item => settings.some(setting => setting.type === item.type));

    if (groupItems.length === 0) {
      return 'all-off';
    }

    const autoCount = groupItems.filter(item => item.status === NOTIFICATION_STATUS_TYPE.AUTO).length;

    if (autoCount === 0) {
      return 'all-off';
    }

    if (autoCount === groupItems.length) {
      return 'all-on';
    }

    return 'mixed';
  };

  renderGroupSettingsRow = (setting: { type: string; name: string }, values, setFieldValue): React.ReactNode => {
    const { isEditMode } = this.state;
    const checkType = this.getCheckType(setting.type);
    const isChecked = values[checkType];
    const isCollapsed = !!this.state.collapsedSections[setting.type];
    const groupDefsCount = SETTINGS.filter(def => def.relation === setting.type).length;
    const visualState = this.getGroupVisualState(values.allSettings, setting.type);

    return (
      <div
        className="nsSection"
        ref={element => {
          this.sectionRefs[setting.type] = element;
        }}
      >
        <div className="nsSectionHeader">
          <div
            className="nsSectionTitleWrap"
            onClick={() => this.toggleSectionCollapsed(setting.type)}
            style={{ cursor: 'pointer' }}
          >
            <div className="nsSectionTitleRow">
              <div className="nsSectionTitle">
                {isCollapsed ? '▸' : '▾'} {setting.name}
              </div>
              <div className="nsRecipientBadge">
                {groupDefsCount} notification{groupDefsCount === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div className="nsToggleCell">
            <div className={`nsSectionToggleWrap nsSectionToggleWrap--${visualState}`}>
              <span className="nsToggleLabel">Off</span>
              <Switch
                key={checkType}
                value={isChecked}
                onChange={() => {
                  this.expandSection(setting.type);
                  this.onGroupSettingsChange(values, setFieldValue, checkType, setting);
                }}
                text={''}
                name={checkType}
                disabled={!isEditMode}
              />
              <span className="nsToggleLabel">On</span>
            </div>
          </div>
        </div>

        {!isCollapsed ? <div className="nsRows">{this.renderSettingsRow(setting, values, setFieldValue)}</div> : null}
      </div>
    );
  };

  renderSettingsRow = (designationSetting: { type: string; name: string }, values, setFieldValue): React.ReactNode => {
    const { isEditMode } = this.state;

    const groupDefs = SETTINGS.filter(def => def.relation === designationSetting.type);

    return groupDefs.map(def => {
      const current = values.allSettings.find(s => s.type === def.type) || {
        type: def.type,
        status: NOTIFICATION_STATUS_TYPE.DISABLED as 'AUTO' | 'DISABLED'
      };

      const isOpensSoon = def.type === NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENS_SOON;

      const isClosesSoon = def.type === NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_CLOSES_SOON;

      const isFixtureReminder = def.type === NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER;
      const isClubReminder = def.type === NOTIFICATION_TYPE.EVENT_CLUB_REMINDER;
      const isHighlightedReminder = this.state.highlightedReminderType === def.type;

      const shortHintText = this.getShortHintText(def.hintText);

      const recipientLabel = this.getRecipientLabel(def.hintText);

      return (
        <div
          key={`setting_type_${def.type}`}
          className={`nsRowShell ${isHighlightedReminder ? 'nsRowShell--error' : ''}`}
        >
          <div className="nsRow">
            <div className="nsRowMain">
              <div className="nsRowTitleLine">
                <div className="nsRowTitle">{def.name}</div>

                {recipientLabel ? <div className="nsRecipientBadge">{recipientLabel}</div> : null}
              </div>
              {shortHintText ? <div className="nsRowHint">{shortHintText}</div> : null}
            </div>

            <div className="nsToggleCell">
              <div className="nsToggleWrap">
                <span className="nsToggleLabel">Off</span>
                <Switch
                  key={def.type}
                  value={this.getIsStatusAuto(current.status)}
                  onChange={() => {
                    this.expandSection(designationSetting.type);

                    const allSettings = [...values.allSettings];
                    const idx = allSettings.findIndex(s => s.type === def.type);

                    const next = {
                      type: current.type,
                      status: this.getReverseStatus(current.status) as 'AUTO' | 'DISABLED'
                    };

                    if (idx === -1) {
                      allSettings.push(next);
                    } else {
                      allSettings.splice(idx, 1, next);
                    }

                    setFieldValue('allSettings', allSettings);

                    const isAutoAll = this.getAllNotificationsStatus(allSettings);
                    setFieldValue('isAllNotifications', isAutoAll);

                    const checkType = this.getCheckType(designationSetting.type);
                    const isGroupAuto = this.getGroupSettingStatus(allSettings, designationSetting.type);
                    setFieldValue(checkType, isGroupAuto);
                  }}
                  text={''}
                  name={def.type}
                  disabled={!isEditMode}
                />
                <span className="nsToggleLabel">On</span>
              </div>
            </div>
          </div>

          {isOpensSoon ? (
            <div className="mb-2">
              {this.renderReminderScheduleInline(
                values,
                setFieldValue,
                'programmeBookingOpensSoonOffsetsMinutes',
                'programmeBookingOpensSoonCustom',
                'Reminder schedule'
              )}
            </div>
          ) : null}

          {isClosesSoon ? (
            <div className="mb-2">
              {this.renderReminderScheduleInline(
                values,
                setFieldValue,
                'programmeBookingClosesSoonOffsetsMinutes',
                'programmeBookingClosesSoonCustom',
                'Reminder schedule'
              )}
            </div>
          ) : null}

          {isFixtureReminder ? (
            <>
              <div className="mb-2">
                {this.renderReminderScheduleInline(
                  values,
                  setFieldValue,
                  'eventFixtureReminderOffsetsMinutes',
                  'eventFixtureReminderCustom',
                  'Reminder schedule'
                )}
              </div>

              <div className="mb-2">
                {this.renderReminderRecipientsInline(
                  values,
                  setFieldValue,
                  'eventFixtureReminderRecipients',
                  'Recipients'
                )}
              </div>
            </>
          ) : null}

          {isClubReminder ? (
            <>
              <div className="mb-2">
                {this.renderReminderScheduleInline(
                  values,
                  setFieldValue,
                  'eventClubReminderOffsetsMinutes',
                  'eventClubReminderCustom',
                  'Reminder schedule'
                )}
              </div>

              <div className="mb-2">
                {this.renderReminderRecipientsInline(
                  values,
                  setFieldValue,
                  'eventClubReminderRecipients',
                  'Recipients'
                )}
              </div>
            </>
          ) : null}
        </div>
      );
    });
  };

  renderSettingsList = (values, setFieldValue): React.ReactNode => {
    const settingsDesignations = [...SETTINGS_DESIGNATIONS];
    return settingsDesignations.map((setting, index) => {
      const isShowLastEmptyRow = settingsDesignations.length - 1 !== index;
      return (
        <div key={`${setting.type}`}>
          {this.renderGroupSettingsRow(setting, values, setFieldValue)}
          {isShowLastEmptyRow && <div className="eNotificationSettingEmptyRow" />}
        </div>
      );
    });
  };

  focusSection = (sectionType: string): void => {
    this.expandSection(sectionType);

    window.setTimeout(() => {
      const element = this.sectionRefs[sectionType];
      if (element && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 0);
  };

  hasUnsavedChanges = (): boolean => {
    const current = this.formValuesRef;
    if (!current) return false;

    const savedSettings = this.state.notificationSettings;
    const savedSchedule = this.state.notificationSchedule;

    const currentSettings = Array.isArray(current.allSettings) ? current.allSettings : [];
    const currentSchedule = current.notificationSchedule || {
      programmeBookingOpensSoonOffsetsMinutes: [],
      programmeBookingClosesSoonOffsetsMinutes: [],
      eventFixtureReminderOffsetsMinutes: [],
      eventFixtureReminderRecipients: [],
      eventClubReminderOffsetsMinutes: [],
      eventClubReminderRecipients: []
    };

    const savedSettingsComparable = JSON.stringify(
      [...savedSettings]
        .map(item => ({ type: item.type, status: item.status }))
        .sort((a, b) => String(a.type).localeCompare(String(b.type)))
    );

    const currentSettingsComparable = JSON.stringify(
      [...currentSettings]
        .map(item => ({ type: item.type, status: item.status }))
        .sort((a, b) => String(a.type).localeCompare(String(b.type)))
    );

    const savedScheduleComparable = JSON.stringify({
      programmeBookingOpensSoonOffsetsMinutes: normalizeReminderOffsets(
        savedSchedule.programmeBookingOpensSoonOffsetsMinutes
      ),
      programmeBookingClosesSoonOffsetsMinutes: normalizeReminderOffsets(
        savedSchedule.programmeBookingClosesSoonOffsetsMinutes
      ),
      eventFixtureReminderOffsetsMinutes: normalizeReminderOffsets(savedSchedule.eventFixtureReminderOffsetsMinutes),
      eventFixtureReminderRecipients: Array.isArray(savedSchedule.eventFixtureReminderRecipients)
        ? [...savedSchedule.eventFixtureReminderRecipients].sort()
        : [],
      eventClubReminderOffsetsMinutes: normalizeReminderOffsets(savedSchedule.eventClubReminderOffsetsMinutes),
      eventClubReminderRecipients: Array.isArray(savedSchedule.eventClubReminderRecipients)
        ? [...savedSchedule.eventClubReminderRecipients].sort()
        : []
    });

    const currentScheduleComparable = JSON.stringify({
      programmeBookingOpensSoonOffsetsMinutes: normalizeReminderOffsets(
        currentSchedule.programmeBookingOpensSoonOffsetsMinutes
      ),
      programmeBookingClosesSoonOffsetsMinutes: normalizeReminderOffsets(
        currentSchedule.programmeBookingClosesSoonOffsetsMinutes
      ),
      eventFixtureReminderOffsetsMinutes: normalizeReminderOffsets(currentSchedule.eventFixtureReminderOffsetsMinutes),
      eventFixtureReminderRecipients: Array.isArray(currentSchedule.eventFixtureReminderRecipients)
        ? [...currentSchedule.eventFixtureReminderRecipients].sort()
        : [],
      eventClubReminderOffsetsMinutes: normalizeReminderOffsets(currentSchedule.eventClubReminderOffsetsMinutes),
      eventClubReminderRecipients: Array.isArray(currentSchedule.eventClubReminderRecipients)
        ? [...currentSchedule.eventClubReminderRecipients].sort()
        : []
    });

    return (
      savedSettingsComparable !== currentSettingsComparable || savedScheduleComparable !== currentScheduleComparable
    );
  };

  onStayOnPage = (): void => {
    this.setState({
      isLeaveWarningOpen: false,
      pendingLeaveLocation: null
    });
  };

  onLeaveAnyway = (): void => {
    const { history } = this.props;
    const { pendingLeaveLocation } = this.state;

    this.setState(
      {
        isLeaveWarningOpen: false,
        isBypassingNavigationBlock: true
      },
      () => {
        if (pendingLeaveLocation) {
          history.push(pendingLeaveLocation);
        } else {
          this.discardChangesAndClose();
        }

        window.setTimeout(() => {
          this.setState({
            pendingLeaveLocation: null,
            isBypassingNavigationBlock: false
          });
        }, 0);
      }
    );
  };

  render() {
    const { notificationSettings, isLoading, isEditMode } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const buttonText = isEditMode ? 'Save' : 'Edit';
    const areAllSectionsCollapsed = SETTINGS_DESIGNATIONS.every(
      setting => !!this.state.collapsedSections[setting.type]
    );

    const notificationSettingsForm = {
      allSettings: notificationSettings,
      notificationSchedule: {
        programmeBookingOpensSoonOffsetsMinutes: this.state.notificationSchedule
          .programmeBookingOpensSoonOffsetsMinutes,
        programmeBookingClosesSoonOffsetsMinutes: this.state.notificationSchedule
          .programmeBookingClosesSoonOffsetsMinutes,
        eventFixtureReminderOffsetsMinutes: this.state.notificationSchedule.eventFixtureReminderOffsetsMinutes,
        eventFixtureReminderRecipients: this.state.notificationSchedule.eventFixtureReminderRecipients,
        eventClubReminderOffsetsMinutes: this.state.notificationSchedule.eventClubReminderOffsetsMinutes,
        eventClubReminderRecipients: this.state.notificationSchedule.eventClubReminderRecipients
      },
      programmeBookingOpensSoonCustomDays: '',
      programmeBookingOpensSoonCustomHours: '',
      programmeBookingOpensSoonCustomMinutes: '',
      programmeBookingClosesSoonCustomDays: '',
      programmeBookingClosesSoonCustomHours: '',
      programmeBookingClosesSoonCustomMinutes: '',
      eventFixtureReminderCustomDays: '',
      eventFixtureReminderCustomHours: '',
      eventFixtureReminderCustomMinutes: '',
      eventClubReminderCustomDays: '',
      eventClubReminderCustomHours: '',
      eventClubReminderCustomMinutes: '',
      isRegistrationAndProfileManagement: this.getGroupSettingStatus(
        notificationSettings,
        SETTING_DESTINATIONS_TYPE.REGISTRATION_AND_PROFILE_MANAGEMENT
      ),
      isUserManagement: this.getGroupSettingStatus(notificationSettings, SETTING_DESTINATIONS_TYPE.USER_MANAGEMENT),
      isEventManagement: this.getGroupSettingStatus(notificationSettings, SETTING_DESTINATIONS_TYPE.EVENT_MANAGEMENT),
      isClubManagement: this.getGroupSettingStatus(notificationSettings, SETTING_DESTINATIONS_TYPE.CLUB_MANAGEMENT),
      isClubProgrammes: this.getGroupSettingStatus(notificationSettings, SETTING_DESTINATIONS_TYPE.CLUB_PROGRAMMES),
      isAttendanceManagement: this.getGroupSettingStatus(
        notificationSettings,
        SETTING_DESTINATIONS_TYPE.ATTENDANCE_MANAGEMENT
      ),
      isAwardManagement: this.getGroupSettingStatus(notificationSettings, SETTING_DESTINATIONS_TYPE.AWARD_MANAGEMENT),
      isInvitesManagement: this.getGroupSettingStatus(
        notificationSettings,
        SETTING_DESTINATIONS_TYPE.INVITES_MANAGEMENT
      ),
      isAbsenceRequests: this.getGroupSettingStatus(notificationSettings, SETTING_DESTINATIONS_TYPE.ABSENCE_REQUESTS),
      isAllNotifications: this.getAllNotificationsStatus(notificationSettings)
    };
    return (
      <Formik initialValues={notificationSettingsForm} onSubmit={this.onFormSubmit}>
        {({ setFieldValue, values }) => {
          this.formValuesRef = values;
          return (
            <>
              <HelpModal
                isOpen={this.state.isHelpOpen}
                title="How school notification settings work"
                badgeText="School notifications"
                subtitle="Use this page to control which automatic school notifications are sent to staff, parents and students, and to choose reminder timings for eligible notifications."
                onClose={() => this.setState({ isHelpOpen: false })}
              >
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">What this page does</div>
                      <div className="text-muted">
                        This page controls the main automatic notifications sent from your school account. You can
                        switch different notification types on or off, and for some reminders you can also choose the
                        timing and recipients.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Who can receive notifications</div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>
                          <b>School staff</b> such as Admins, Managers, Teachers and Coaches
                        </li>
                        <li>
                          <b>Parents</b> linked to students
                        </li>
                        <li>
                          <b>Students</b> on the school account
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Registration and profile management</div>
                      <div className="text-muted">
                        These notifications relate to role requests and access to the school account. Examples include
                        role requests being approved or denied, or users being granted new permissions.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">User management</div>
                      <div className="text-muted">
                        These notifications help school staff manage account activity, such as new role requests and
                        account merges for parents and students.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Event management</div>
                      <div className="text-muted mb-2">
                        This section covers notifications linked to fixtures, meets, challenges and event teamsheets.
                      </div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>event created, updated or cancelled</li>
                        <li>players added, removed or updated</li>
                        <li>consent requests</li>
                        <li>reports, scores and assigned tasks</li>
                        <li>custom event notifications</li>
                        <li>fixture reminders</li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Club management</div>
                      <div className="text-muted mb-2">
                        This section covers the main notifications for school clubs.
                      </div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>club created</li>
                        <li>club activated</li>
                        <li>club invitation received</li>
                        <li>waiting list notifications</li>
                        <li>club session reminders</li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Activitiy programmes</div>
                      <div className="text-muted mb-2">
                        These notifications relate to the activity programme booking journey.
                      </div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>
                          <b>Booking submitted</b> is sent to school admins when a booking is submitted
                        </li>
                        <li>
                          <b>Booking opened</b> is sent when the booking window opens
                        </li>
                        <li>
                          <b>Booking opens soon / closes soon</b> are reminder notifications
                        </li>
                        <li>
                          <b>Club allocations confirmed</b> is sent when final allocations are activated
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Attendance, awards, invites and absence</div>
                      <div className="text-muted">
                        Other sections cover attendance updates, awards and house points, incoming and outgoing event
                        invites, and absence request activity for schools and parents.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">How reminder timing works</div>
                      <div className="text-muted mb-2">Some notification types include reminders, for example:</div>
                      <ul className="mb-0 pl-3 text-muted">
                        <li>activity programme booking opens soon</li>
                        <li>activity programme booking closes soon</li>
                        <li>fixture reminder</li>
                        <li>club session reminder</li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Reminder intervals</div>
                      <div className="text-muted">
                        For reminder-based notifications, you can choose one or more intervals before the event or
                        booking window. You can use preset options like 7 days, 1 day or 1 hour, or add your own custom
                        interval using days, hours and minutes.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Reminder recipients</div>
                      <div className="text-muted">
                        Fixture reminders and club reminders also let you choose who should receive them. Depending on
                        the type of reminder, you can send them to parents, students and/or staff.
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <div className="border rounded p-3 h-100">
                      <div className="font-weight-bold mb-2">Using the switches</div>
                      <div className="text-muted">
                        You can turn notifications on or off individually, by whole section, or all at once. Section
                        switches are helpful when you want to enable or disable a full category of notifications
                        together.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border rounded p-3 mt-2" style={{ background: '#fffdf3' }}>
                  <div className="font-weight-bold mb-2">Important</div>
                  <div className="text-muted mb-0">
                    A reminder notification will only work when the notification type itself is switched on. For fixture
                    reminders and club reminders, you also need to choose at least one recipient. For reminder timings,
                    at least one interval must be selected.
                  </div>
                </div>
              </HelpModal>
              <StatusModal
                isOpen={this.state.isStatusModalOpen}
                compact
                status={this.state.statusModalStatus}
                title={this.state.statusModalTitle}
                message={this.state.statusModalMessage}
                lines={this.state.statusModalLines}
                onClose={() =>
                  this.setState({
                    isStatusModalOpen: false,
                    statusModalTitle: '',
                    statusModalMessage: '',
                    statusModalLines: [],
                    statusModalStatus: 'success'
                  })
                }
              />

              <Form>
                <div className="nsPage">
                  <div className="container-fluid">
                    <div className="row">
                      <div className="col-md-12 eNotificationSettings">
                        <div className="nsTopBar nsPageHeader">
                          <div className="nsTopBarMain">
                            <div className="nsHeadingRow">
                              <h1 className="nsTitle">Notification settings</h1>
                            </div>

                            <div className="nsSubtitle">
                              Manage which system notifications are sent to staff, parents and students. Reminder
                              schedules for eligible notification types can be configured inline.
                            </div>
                          </div>

                          <div className="nsTopBarActions">
                            <button
                              type="button"
                              className="nsButton nsButton--secondary"
                              onClick={areAllSectionsCollapsed ? this.expandAllSections : this.collapseAllSections}
                              aria-expanded={!areAllSectionsCollapsed}
                            >
                              {areAllSectionsCollapsed ? 'Expand all' : 'Collapse all'}
                            </button>

                            <button
                              type="button"
                              className="nsButton nsButton--secondary"
                              disabled={!isEditMode}
                              onClick={this.onCloseClick}
                            >
                              Cancel
                            </button>

                            <button type="submit" className="nsButton nsButton--primary">
                              {buttonText}
                            </button>
                          </div>
                        </div>

                        <div className="nsCard">
                          <div>
                            <div className="nsTableHead">
                              <div className="nsTableHeadTitle">Notification</div>
                              <div className="nsTableHeadTitle">Status</div>
                            </div>

                            <div className="nsAllRow">
                              <div className="nsAllRowInner">
                                <div>
                                  <div className="nsAllTitle">All notifications</div>
                                  <div className="nsAllHint">Turn all available notification types on or off.</div>
                                </div>

                                <div className="nsToggleCell">
                                  <div className="nsToggleWrap">
                                    <span className="nsToggleLabel">Off</span>
                                    <Switch
                                      key={'isAllNotifications'}
                                      value={values.isAllNotifications}
                                      onChange={() => this.onAllNotificationsChange(setFieldValue, values)}
                                      text={''}
                                      name="isAllNotifications"
                                      disabled={!isEditMode}
                                    />
                                    <span className="nsToggleLabel">On</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {this.renderSettingsList(values, setFieldValue)}
                          </div>
                        </div>

                        <UnsavedChangesModal
                          isOpen={this.state.isLeaveWarningOpen}
                          message="Your notification settings have changed. Discarding will restore the last saved settings."
                          onKeepEditing={this.onStayOnPage}
                          onDiscard={this.onLeaveAnyway}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    );
  }
}
